import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { cat_id2lbl } from '../../lib/cat';
import CreatorHeader from '../../components/creator/CreatorHeader';
import PlatformIcon, { platform_lbl } from '../../components/PlatformIcon';
import platform from '../../lib/platform';

import xdate from '../../../../../util/date.js';

type CreatorTier = {
  enabled: boolean;
  payment: number;
};

type Campaign = {
  id: string;
  name: string;
  description: string;
  category: string;
  brand_name: string;
  brand_logo_url?: string;
  asset_types: {
    video_ad: number;
    banner: number;
  };
  budget_info: {
    total_budget: number;
    spent: number;
    remaining: number;
    creator_tiers?: {
      small?: CreatorTier;
      medium?: CreatorTier;
      large?: CreatorTier;
    };
  };
  targeting: {
    countries?: string[];
    age_ranges?: number[][];
    genders?: string[];
    content_categories?: string[];
    platforms?: string[];
  };
  sched: {
    is_always_on: boolean;
    start_date?: string;
    end_date?: string;
  };
  allowed_ad_formats: string[];
  active_creators_count: number;
  match_score: number;
  is_active?: boolean;
  status?: string;
  creator_tier?: 'small' | 'medium' | 'large';
  already_joined?: boolean;
};

type Asset = {
  id: string;
  name: string;
  asset_type: string;
  file_url: string;
};

type Video = {
  id: string;
  title: string;
  thumbnail_url?: string;
  duration: number;
  video_url: string;
  ts?: {insert: Date, modified: Date};
};


function VideoCard({ 
  video, 
  isSelected, 
  onSelect, 
  onPreview 
}: { 
  video: Video; 
  isSelected: boolean; 
  onSelect: () => void; 
  onPreview: (e: React.MouseEvent) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);

  const handleMouseEnter = () => {
    if (videoRef.current && !videoError) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div className="relative">
      <div
        onClick={onSelect}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`w-full rounded-lg border-2 overflow-hidden transition-all cursor-pointer ${
          isSelected
            ? 'border-blue-500 bg-blue-50 shadow-lg'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-700 relative group overflow-hidden">
          {!videoError && video.video_url ? (
            <video
              ref={videoRef}
              src={video.video_url}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
              onError={() => setVideoError(true)}
            />
          ) : video.thumbnail_url ? (
            <img
              src={video.thumbnail_url}
              alt={video.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 text-white opacity-50" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
              </svg>
            </div>
          )}

          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPreview(e);
              }}
              className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
              </svg>
            </button>
          </div>

          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
            {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
          </div>
        </div>

        <div className="p-3">
          <h3 className="font-semibold text-gray-900 mb-1 truncate text-sm sm:text-base">
            {video.title}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500">
            {video.ts?.insert ? xdate.fmt_lbl_short(video.ts.insert) : ''}
          </p>
        </div>

      </div>

      <div
        role="checkbox"
        aria-checked={isSelected}
        aria-label={isSelected ? `Remove ${video.title}` : `Add ${video.title}`}
        title={isSelected ? 'Remove' : 'Add'}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className={`absolute top-2 right-2 w-9 h-9 rounded-md flex items-center justify-center shadow-lg z-10 cursor-pointer border-2 transition-all ${
          isSelected
            ? 'bg-blue-500 border-blue-500 hover:bg-blue-600'
            : 'bg-white border-gray-300 hover:border-blue-400'
        }`}
      >
        {isSelected && (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
          </svg>
        )}
      </div>
    </div>
  );
}


function ratio_lbl(w: number, h: number): string {
  if (!w || !h) return '';
  const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a;
  const g = gcd(w, h);
  return `${w / g}:${h / g}`;
}

function dur_lbl(sec: number): string {
  if (!sec || !isFinite(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = String(Math.floor(sec % 60)).padStart(2, '0');
  return `${m}:${s}`;
}

function AssetCard({
  asset,
  is_checked,
  on_toggle,
  on_preview,
}: {
  asset: Asset;
  is_checked: boolean;
  on_toggle: () => void;
  on_preview: () => void;
}) {
  const video_ref = useRef<HTMLVideoElement>(null);
  const [meta, set_meta] = useState<{w: number, h: number, dur: number} | null>(null);

  const handle_loaded = () => {
    const v = video_ref.current;
    if (!v) return;
    set_meta({w: v.videoWidth, h: v.videoHeight, dur: v.duration});
  };

  const handle_enter = () => {
    if (video_ref.current) video_ref.current.play().catch(() => {});
  };

  const handle_leave = () => {
    if (video_ref.current) {
      video_ref.current.pause();
      video_ref.current.currentTime = 0;
    }
  };

  const is_video = asset.asset_type === 'video_ad';

  return (
    <div className="relative">
      <button
        onClick={on_toggle}
        onMouseEnter={is_video ? handle_enter : undefined}
        onMouseLeave={is_video ? handle_leave : undefined}
        className={`w-full rounded-lg border-2 overflow-hidden transition-all ${
          is_checked
            ? 'border-blue-500 bg-blue-50 shadow-xl scale-105'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="aspect-video bg-gray-100 flex items-center justify-center relative group overflow-hidden">
          {is_video ? (
            <video
              ref={video_ref}
              src={asset.file_url}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedMetadata={handle_loaded}
            />
          ) : (
            <img
              src={asset.file_url}
              alt={asset.name}
              className="max-w-full max-h-full object-contain"
            />
          )}

          <div
            onClick={(e) => {
              e.stopPropagation();
              on_preview();
            }}
            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
          >
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>

          {is_video && meta && (
            <>
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
                {ratio_lbl(meta.w, meta.h)}
              </div>
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
                {dur_lbl(meta.dur)}
              </div>
            </>
          )}
        </div>

        <div className="p-3 bg-white">
          <p className="text-sm font-medium text-gray-900 truncate">
            {asset.name}
          </p>
        </div>
      </button>

      <div
        role="checkbox"
        aria-checked={is_checked}
        aria-label={is_checked ? `Remove ${asset.name}` : `Add ${asset.name}`}
        title={is_checked ? 'Remove' : 'Add'}
        onClick={(e) => {
          e.stopPropagation();
          on_toggle();
        }}
        className={`absolute top-2 right-2 w-9 h-9 rounded-md flex items-center justify-center shadow-lg z-10 cursor-pointer border-2 transition-all ${
          is_checked
            ? 'bg-blue-500 border-blue-500 hover:bg-blue-600'
            : 'bg-white border-gray-300 hover:border-blue-400'
        }`}
      >
        {is_checked && (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
          </svg>
        )}
      </div>
    </div>
  );
}

export default function CampaignDetails() {
  const navigate = useNavigate();
  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [myVideos, setMyVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [selectedAsset, setSelectedAsset] = useState<string>('');
  const [selectedAssetType, setSelectedAssetType] = useState<'video_ad' | 'banner'>('video_ad');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [socials, set_socials] = useState<Array<{platform: string, avg_views?: number, med_views?: number, cpm?: number, value: number}>>([]);
  
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  
  const [videoSearch, setVideoSearch] = useState('');
  const [videoSortOrder, setVideoSortOrder] = useState<'newest' | 'oldest'>('newest');
  
  const [previewVideoInModal, setPreviewVideoInModal] = useState<Video | null>(null);
  const [showNoVideosModal, setShowNoVideosModal] = useState(false);

  useEffect(() => {
    if (campaignId) {
      fetchCampaignDetails();
      fetchMyVideos();
      fetch_status();
    }
  }, [campaignId]);

  const fetch_status = async () => {
    try {
      const status_res = await back_app.creator_status_get();
      if (!status_res?.err)
        set_socials(status_res?.socials || []);
    } catch (e) {
      console.error('status fetch error', e);
    }
  };

  const fetchCampaignDetails = async () => {
    try {
      const campaignResult = await back_app.campaign_get(campaignId);

      if (!campaignResult.err) {
        setCampaign(campaignResult);
      }

      const assetsResult = await back_app.campaign_asset_list(campaignId);

      if (!assetsResult.err) {
        setAssets(assetsResult || []);
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      if ((error as Error).message == 'no session') {
        navigate('/login');
      } else {
        alert('Failed to load campaign details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMyVideos = async () => {
    try {
      const result = await back_app.video_list();

      if (!result.err) {
        setMyVideos(result || []);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const filteredVideos = useMemo(() => {
    let filtered = myVideos;

    if (videoSearch) {
      const query = videoSearch.toLowerCase();
      filtered = filtered.filter(v => 
        v.title.toLowerCase().includes(query)
      );
    }

    filtered = [...filtered].sort((a, b) => {
      const dateA = a.ts?.insert?.getTime() || 0;
      const dateB = b.ts?.insert?.getTime() || 0;
      return videoSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [myVideos, videoSearch, videoSortOrder]);

  const handleJoinCampaign = () => {
    if (campaign?.already_joined) {
      alert('You have already joined this campaign');
      return;
    }
    if (!selectedAsset) {
      alert('Please select an asset first');
      return;
    }

    if (!campaign?.creator_tier) {
      alert('Your creator tier is not set yet');
      return;
    }

    if (myVideos.length === 0) {
      setShowNoVideosModal(true);
      return;
    }

    setShowVideoModal(true);
  };

  const handleConnectAndProcess = async () => {
    if (!selectedVideo) {
      alert('Please select a video');
      return;
    }

    setProcessing(true);
    setShowVideoModal(false);

    const ad_type = selectedAssetType == 'video_ad' ? 'break' : 'logo';

    try {
      const linkPayload = {
        video_id: selectedVideo,
        campaign_id: campaignId,
        selected_asset_id: selectedAsset,
        ad_type,
        creator_tier: campaign?.creator_tier
      };

      const linkResult = await back_app.vcamp_insert(linkPayload);

      if (linkResult.err) {
        throw new Error(linkResult.err || 'Failed to create video-campaign link');
      }

      const video_campaign_id = linkResult.id;

      navigate(`/creator/video-processing?id=${video_campaign_id}&video_id=${selectedVideo}&campaign_id=${campaignId}`);

      (async () => {
        try {
          const analyzeResult = await back_app.vcamp_analyze({ id: video_campaign_id });
          if (analyzeResult.err)
            throw new Error(analyzeResult.err);
          const best_suggestion = analyzeResult.analysis?.suggestions?.[0];
          if (!best_suggestion)
            throw new Error('No analysis suggestions');
          await back_app.vcamp_select_placement({
            id: video_campaign_id,
            timestamp: best_suggestion.timestamp,
            ad_type
          });
          await back_app.vcamp_process({ id: video_campaign_id });
        } catch (bg_err) {
          console.error('background processing error:', bg_err);
        }
      })();

    } catch (error: any) {
      console.error('Error:', error);
      alert(`Error: ${error.message || 'Failed to start processing'}`);
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Campaign not found</h2>
            <button
              onClick={() => navigate('/creator/campaigns')}
              className="text-blue-600 hover:underline"
            >
              Back to campaigns
            </button>
          </div>
        </div>
      </div>
    );
  }

  const videoAds = assets.filter(a => a.asset_type === 'video_ad');
  const banners = assets.filter(a => a.asset_type === 'banner');
  const currentAssets = selectedAssetType === 'video_ad' ? videoAds : banners;

  const tiers = campaign.budget_info.creator_tiers || {};
  const creator_tier = campaign.creator_tier;
  const tier_data = creator_tier ? tiers[creator_tier] : undefined;

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={() => navigate('/creator/campaigns')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <span>←</span>
          <span>Back to Campaigns</span>
        </button>

        {}
        <div className="bg-white rounded-xl border shadow-sm p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex flex-col items-center gap-3">
              <div className="w-20 h-20 bg-white rounded-xl border-2 border-blue-200 flex items-center justify-center flex-shrink-0">
                {campaign.brand_logo_url ? (
                  <img
                    src={campaign.brand_logo_url}
                    alt={campaign.brand_name}
                    className="w-16 h-16 object-contain"
                  />
                ) : (
                  <svg className="w-10 h-10 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/>
                  </svg>
                )}
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-blue-600">
                  {campaign.brand_name}
                </p>
              </div>
            </div>

            <div className="flex-1">
              <div className="mb-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                  {campaign.name}
                </h1>
              </div>

              <p className="text-gray-600 mb-4">{campaign.description}</p>

              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                  {cat_id2lbl(campaign.category)}
                </span>
                {campaign.status == 'active' && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                    Active
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border p-4">
            <div className="text-sm text-gray-500 mb-1">Total Budget</div>
            <div className="text-2xl font-bold text-gray-900">
              ${campaign.budget_info.total_budget.toLocaleString()}
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="text-sm text-gray-500 mb-1">Remaining</div>
            <div className="text-2xl font-bold text-green-600">
              ${campaign.budget_info.remaining.toLocaleString()}
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4 col-span-2 lg:col-span-1">
            <div className="text-sm text-gray-500 mb-1">Active Creators</div>
            <div className="text-2xl font-bold text-purple-600">
              {campaign.active_creators_count}
            </div>
          </div>
        </div>

        {socials.length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Estimated payout per platform</h2>
            <div className="flex flex-wrap gap-3">
              {socials.map((s) => (
                <div
                  key={s.platform}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg"
                >
                  <PlatformIcon platform={s.platform} className="w-7 h-7"/>
                  <div>
                    <div className="text-xs text-gray-600">{platform_lbl(s.platform)}</div>
                    <div className="text-sm font-bold text-green-600">
                      ${platform.payout_est(s).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Targeting</h3>
            
            <div className="space-y-3">
              {campaign.targeting.countries && campaign.targeting.countries.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Countries</div>
                  <div className="flex flex-wrap gap-2">
                    {campaign.targeting.countries.map((country) => (
                      <span key={country} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                        {country}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {campaign.targeting.age_ranges && campaign.targeting.age_ranges.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Age Range</div>
                  <div className="flex flex-wrap gap-2">
                    {campaign.targeting.age_ranges.map((range, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded">
                        {range[0]} - {range[1]} years
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {campaign.targeting.content_categories && campaign.targeting.content_categories.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Content Categories</div>
                  <div className="flex flex-wrap gap-2">
                    {campaign.targeting.content_categories.map((cat) => (
                      <span key={cat} className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded">
                        {cat_id2lbl(cat)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(!campaign.targeting.countries || campaign.targeting.countries.length === 0) &&
               (!campaign.targeting.content_categories || campaign.targeting.content_categories.length === 0) &&
               (!campaign.targeting.age_ranges || campaign.targeting.age_ranges.length === 0) && (
                <div className="text-sm text-gray-500">No specific targeting set</div>
              )}
            </div>
          </div>

          {}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Schedule</h3>
            
            {campaign.sched.is_always_on ? (
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                <span className="font-semibold">Always Active</span>
              </div>
            ) : (
              <div className="space-y-2">
                {campaign.sched.start_date && (
                  <div>
                    <div className="text-sm text-gray-500">Start Date</div>
                    <div className="font-semibold text-gray-900">
                      {xdate.fmt_lbl_short(campaign.sched.start_date)}
                    </div>
                  </div>
                )}
                {campaign.sched.end_date && (
                  <div>
                    <div className="text-sm text-gray-500">End Date</div>
                    <div className="font-semibold text-gray-900">
                      {xdate.fmt_lbl_short(campaign.sched.end_date)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {}
        <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Select Asset Type</h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => {
                setSelectedAssetType('video_ad');
                setSelectedAsset('');
              }}
              className={`py-4 px-6 rounded-lg font-semibold transition-all ${
                selectedAssetType === 'video_ad'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="text-2xl mb-1">📺</div>
              <div>Video Ad</div>
              <div className="text-sm opacity-75">({videoAds.length} available)</div>
            </button>

            <button
              onClick={() => {
                setSelectedAssetType('banner');
                setSelectedAsset('');
              }}
              className={`py-4 px-6 rounded-lg font-semibold transition-all ${
                selectedAssetType === 'banner'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="text-2xl mb-1">🖼️</div>
              <div>Static Banner</div>
              <div className="text-sm opacity-75">({banners.length} available)</div>
            </button>
          </div>

          {currentAssets.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {currentAssets.map((asset) => {
                const is_checked = selectedAsset === asset.id;
                return (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    is_checked={is_checked}
                    on_toggle={() => setSelectedAsset(is_checked ? '' : asset.id)}
                    on_preview={() => setPreviewAsset(asset)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-2">📭</div>
              <p className="text-gray-600">
                No {selectedAssetType === 'video_ad' ? 'video ads' : 'banners'} available
              </p>
            </div>
          )}
        </div>

        {}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Ready to Join?
              </h3>
              <p className="text-gray-700 mb-3">
                Choose an asset and pick your video
              </p>
            </div>

            <button
              onClick={handleJoinCampaign}
              disabled={!!campaign.already_joined || !selectedAsset || !creator_tier || !tier_data?.enabled || processing}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold text-lg hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {campaign.already_joined ? 'Already Joined' : 'Join Campaign →'}
            </button>
          </div>
        </div>
      </div>

      {}
      {previewAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{previewAsset.name}</h3>
              <button
                onClick={() => setPreviewAsset(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>
            <div className="p-6 flex items-center justify-center bg-gray-100">
              {previewAsset.asset_type === 'video_ad' ? (
                <video
                  src={previewAsset.file_url}
                  controls
                  autoPlay
                  className="max-w-full max-h-[70vh] rounded-lg"
                />
              ) : (
                <img
                  src={previewAsset.file_url}
                  alt={previewAsset.name}
                  className="max-w-full max-h-[70vh] rounded-lg"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 border-b flex-shrink-0">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Select Your Video
                </h2>
                <button
                  onClick={() => {
                    setShowVideoModal(false);
                    setPreviewVideoInModal(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <input
                  type="text"
                  placeholder="Search videos..."
                  value={videoSearch}
                  onChange={(e) => setVideoSearch(e.target.value)}
                  className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={videoSortOrder}
                  onChange={(e) => setVideoSortOrder(e.target.value as 'newest' | 'oldest')}
                  className="px-3 sm:px-4 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>

            <div className="p-3 sm:p-6 overflow-y-auto flex-1">
              {filteredVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {filteredVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      isSelected={selectedVideo === video.id}
                      onSelect={() => setSelectedVideo(selectedVideo === video.id ? '' : video.id)}
                      onPreview={(e) => {
                        e.stopPropagation();
                        setPreviewVideoInModal(video);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl sm:text-6xl mb-4">🔍</div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                    No videos found
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    {videoSearch ? 'Try a different search term' : 'Upload your first video to join campaigns'}
                  </p>
                </div>
              )}
            </div>

            {filteredVideos.length > 0 && (
              <div className="p-4 sm:p-6 border-t bg-gray-50 flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setShowVideoModal(false);
                      setPreviewVideoInModal(null);
                    }}
                    className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-semibold text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConnectAndProcess}
                    disabled={!selectedVideo || processing}
                    className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {processing ? 'Processing...' : 'Continue →'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {}
      {previewVideoInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-2 sm:p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl overflow-hidden">
            <div className="p-3 sm:p-4 border-b flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate pr-2">
                {previewVideoInModal.title}
              </h3>
              <button
                onClick={() => setPreviewVideoInModal(null)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-2"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>
            <div className="p-2 sm:p-6 flex items-center justify-center bg-black">
              <video
                src={previewVideoInModal.video_url}
                controls
                autoPlay
                className="w-full max-h-[60vh] sm:max-h-[75vh] rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {}
      {showNoVideosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6 sm:p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Upload a video first
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                You need to upload at least one video before you can join this campaign.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowNoVideosModal(false)}
                  className="w-full sm:flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-semibold text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowNoVideosModal(false);
                    navigate(`/creator/upload?campaign=${encodeURIComponent(campaignId || '')}`);
                  }}
                  className="w-full sm:flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg text-sm sm:text-base"
                >
                  Upload Video
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}